import React, { useMemo, useState } from 'react';
import {
  userDisplayName,
  STATUS_LABELS,
  STATUS_BADGE_CLASS,
  PRIORITY_LABELS,
  PRIORITY_BADGE_CLASS,
  taskCode,
  formatDate,
  isOverdue,
  avancementTache,
  libelleBlocage,
  joursDeBlocage,
  niveauEscalade,
  userInitials,
} from './helpers';

// Ordre volontairement identique à celui du Kanban, pour que l'œil retrouve
// les mêmes colonnes dans le même ordre en passant d'une vue à l'autre.
const STATUTS = ['Todo', 'In_Progress', 'In_Review', 'Done', 'Blocked'];

// Du plus urgent au moins urgent, pour le tri.
const RANG_PRIORITE = { Urgent: 0, High: 1, Medium: 2, Low: 3 };

const TRIS = {
  echeance: 'Trier par échéance',
  priorite: 'Trier par priorité',
  reference: 'Trier par référence',
};

function TaskListView({ tasks, users, onOpenTask, commentCounts = {}, attachmentCounts = {} }) {
  const [filtreStatut, setFiltreStatut] = useState('tous');
  const [tri, setTri] = useState('reference');

  // Compteurs calculés sur la liste complète : ils ne doivent pas changer
  // quand on filtre, sinon les pastilles deviennent illisibles.
  const compteurs = useMemo(() => {
    const c = { tous: tasks.length };
    STATUTS.forEach((s) => {
      c[s] = tasks.filter((t) => t.status === s).length;
    });
    return c;
  }, [tasks]);

  const visibles = useMemo(() => {
    let liste = filtreStatut === 'tous' ? tasks : tasks.filter((t) => t.status === filtreStatut);

    liste = [...liste].sort((a, b) => {
      if (tri === 'priorite') {
        const ra = RANG_PRIORITE[a.priority] ?? 9;
        const rb = RANG_PRIORITE[b.priority] ?? 9;
        if (ra !== rb) return ra - rb;
        return a.id - b.id;
      }
      if (tri === 'echeance') {
        // Les tâches sans échéance passent en dernier plutôt qu'en tête.
        return String(a.due_date || '9999-12-31').localeCompare(String(b.due_date || '9999-12-31'));
      }
      return a.id - b.id;
    });

    return liste;
  }, [tasks, filtreStatut, tri]);

  if (tasks.length === 0) {
    return (
      <div className="empty-state">
        Aucune tâche pour ce projet.
        <div className="empty-state-action">
          <button className="btn btn-primary" onClick={() => onOpenTask(null, 'Todo')}>+ Ajouter une tâche</button>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header-row">
        <div className="filter-pills">
          <button
            className={`filter-pill ${filtreStatut === 'tous' ? 'active' : ''}`}
            onClick={() => setFiltreStatut('tous')}
          >
            Toutes ({compteurs.tous})
          </button>
          {STATUTS.filter((s) => compteurs[s] > 0).map((s) => (
            <button
              key={s}
              className={`filter-pill ${filtreStatut === s ? 'active' : ''}`}
              onClick={() => setFiltreStatut(s)}
            >
              {STATUS_LABELS[s]} ({compteurs[s]})
            </button>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <select value={tri} onChange={(e) => setTri(e.target.value)} className="btn btn-sm">
            {Object.entries(TRIS).map(([cle, libelle]) => (
              <option key={cle} value={cle}>{libelle}</option>
            ))}
          </select>
          <button className="btn btn-primary btn-sm" onClick={() => onOpenTask(null, 'Todo')}>+ Ajouter une tâche</button>
        </div>
      </div>

      {visibles.length === 0 ? (
        <div className="empty-state">Aucune tâche dans cette catégorie.</div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Réf.</th>
                <th>Tâche</th>
                <th>Responsable</th>
                <th>Priorité</th>
                <th>Statut</th>
                <th>Échéance</th>
                <th>Avancement</th>
                <th>Commentaires</th>
                <th>Documents</th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((task) => {
                const avancement = avancementTache(task);
                return (
                  <tr key={task.id} className="clickable" onClick={() => onOpenTask(task)}>
                    <td>{taskCode(task.id)}</td>
                    <td>{task.name}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="avatar">
                          {userInitials(users.find((u) => u.id === task.assigned_to))}
                        </div>
                        <div>
                          {userDisplayName(users.find((u) => u.id === task.assigned_to))}
                          {task.interim_name && (
                            <div style={{ fontSize: 11, color: 'var(--accent)' }}>
                              intérim : {task.interim_name}
                            </div>
                          )}
                          {task.delegated_to && (
                            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                              déléguée à {userDisplayName(users.find((u) => u.id === task.delegated_to))}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className={`badge ${PRIORITY_BADGE_CLASS[task.priority] || 'badge-priority-medium'}`}>
                        {PRIORITY_LABELS[task.priority] || task.priority || '-'}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${STATUS_BADGE_CLASS[task.status] || 'badge-todo'}`}>
                        {STATUS_LABELS[task.status] || task.status}
                      </span>
                      {task.status === 'Blocked' && (
                        <div
                          className={`blocage-ligne blocage-ligne-${niveauEscalade(joursDeBlocage(task)) || 'normal'}`}
                        >
                          {libelleBlocage(task)}
                        </div>
                      )}
                    </td>
                    <td><span className={isOverdue(task) ? 'date-retard' : ''}>{formatDate(task.due_date)}</span></td>
                    <td style={{ width: 140 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div className="progress-bar" style={{ flex: 1, margin: 0 }}>
                          <div className="progress-fill" style={{ width: `${avancement}%` }} />
                        </div>
                        <span style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{avancement}%</span>
                      </div>
                    </td>
                    <td>💬 {commentCounts[task.id] || 0}</td>
                    <td>📎 {attachmentCounts[task.id] || 0}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default TaskListView;
